/**
 * The workflows library grid — PURE (props in, JSX out). No Convex hooks, no
 * navigation: the `/workflows` route feeds `useSpecs()` data in and wires
 * `onOpen` / `onNewWorkflow` intent out.
 *
 * Ported from the `WorkflowsLibraryMockup` grid, minus the demo nav rail and
 * the static "4 live, 1 paused" copy. The dashed "Describe a new job" card is
 * the grid TAIL and doubles as the empty state: with zero specs it is the
 * whole content (workflows-dedicated-ui §3.2).
 */

import { Plus } from "lucide-react";

import {
  WorkflowCard,
  type WorkflowCardExecution,
  type WorkflowCardSpec,
} from "@/components/workflows/WorkflowCard";

export interface WorkflowLibraryItem {
  readonly spec: WorkflowCardSpec;
  readonly latestExecution: WorkflowCardExecution | null;
}

export interface WorkflowLibraryProps {
  readonly specs: readonly WorkflowLibraryItem[];
  readonly onOpen: (specId: string) => void;
  readonly onNewWorkflow: () => void;
  readonly isLoading?: boolean;
}

function NewWorkflowCard({ onNewWorkflow }: { onNewWorkflow: () => void }) {
  return (
    <button
      type="button"
      onClick={onNewWorkflow}
      className="flex min-h-[172px] flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-home-ink/15 dark:border-white/15 font-body text-[13px] font-semibold text-home-ink/45 dark:text-white/45 transition-colors hover:border-home-ink/30 dark:hover:border-white/30 hover:text-home-ink/70 dark:hover:text-white/70"
    >
      <Plus className="h-5 w-5" strokeWidth={2.4} />
      Describe a new job
    </button>
  );
}

export function WorkflowLibrary({
  specs,
  onOpen,
  onNewWorkflow,
  isLoading = false,
}: WorkflowLibraryProps) {
  return (
    <div className="mx-auto max-w-6xl 2xl:max-w-none">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-display text-[26px] font-semibold tracking-tight text-home-ink dark:text-white">
            Workflows
          </h2>
          <p className="mt-1 font-body text-[14px] text-home-ink/55 dark:text-white/55">
            Everything your agent runs for you.
          </p>
        </div>
        <button
          type="button"
          onClick={onNewWorkflow}
          className="inline-flex items-center gap-1.5 rounded-full bg-home-yellow px-4 py-2 font-body text-[13px] font-bold text-home-ink shadow-[0_8px_20px_-10px_rgba(33,31,28,0.35)]"
        >
          <Plus className="h-4 w-4" strokeWidth={2.6} />
          New workflow
        </button>
      </div>

      {isLoading ? (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="min-h-[172px] animate-pulse rounded-3xl border border-home-ink/8 dark:border-white/8 bg-home-ink/[0.03] dark:bg-white/[0.03]"
            />
          ))}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {specs.map((item, index) => (
            <WorkflowCard
              key={item.spec._id}
              spec={item.spec}
              latestExecution={item.latestExecution}
              onOpen={onOpen}
              index={index}
            />
          ))}
          {/* Grid tail + empty state: with no specs this dashed card is the
              whole content the user sees. */}
          <NewWorkflowCard onNewWorkflow={onNewWorkflow} />
        </div>
      )}
    </div>
  );
}
